import { useMemo, useState } from 'react';
import clsx from 'clsx';
import SweeperWorkspace from '@/components/queue/SweeperWorkspace';
import MissedDepositsList from '@/components/queue/MissedDepositsList';
import SweeperCallbacks from '@/components/queue/SweeperCallbacks';
import UnfinishedBusinessList from '@/components/queue/UnfinishedBusinessList';
import { buildSweeperCallbacks, buildSweeperUnfinishedBusiness } from '@/domain/sweeperOutcomes';
import { currentActorId } from '@/domain/actor';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { buildMissedDeposits } from '@/domain/missedDeposits';
import { activeStudioIds } from '@/domain/studioScope';
import { useDepositsView } from '@/api/views';
import { ALL_STUDIOS as ALL_STUDIO_IDS } from '@/data/personas';
import { useSimulationStore } from '@/state/simulationStore';

type SweeperTab = 'fill' | 'missed' | 'unfinished';

/**
 * Operations Lead sweeper — one screen (bd tv-ch5 + tv-d2f, board 08). Two
 * top-level tabs: "Fill this week's gaps" (artist-at-a-time gap ladder) and
 * "Missed deposits" (unpaid gap-fill links past their 24h lock, the morning
 * priority; 48h+ unclosed escalates to Ops). The old "Money & open business"
 * cohort list is retired — deposit-on-file and open business are rungs of the
 * fill ladder.
 */
export default function Sweeper() {
  const [tab, setTab] = useState<SweeperTab>('fill');

  // Live reads (tv-ods.3): the missed-deposits list reads deposits + their
  // referenced bookings/customers from the network-wide deposits view (the same
  // /api/views/deposits + /ws invalidation the Deposits Ledger uses), instead of
  // the in-browser brainStore. Writes still flow through SweeperWorkspace.
  const view = useDepositsView();
  const deposits = view.data?.deposits ?? {};
  const customers = view.data?.customers ?? {};
  const bookings = view.data?.bookings ?? {};
  const clockIso = useSimulationStore((s) => s.clockIso);

  // branding-3: network-wide, less any archived studio.
  const storeStudios = useBrainStore((s) => s.studios);
  const liveStudioIds = useMemo(() => activeStudioIds(storeStudios, ALL_STUDIO_IDS), [storeStudios]);
  const missed = useMemo(
    () => buildMissedDeposits({ nowIso: clockIso, deposits, customers, bookings, studioIds: liveStudioIds }),
    [clockIso, deposits, customers, bookings, liveStudioIds],
  );

  // B7: the Callbacks band (75nh D08-26) and Unfinished business (75mt) read the
  // store, like the fill ladder. Persisted outcomes, so both survive a reload.
  const persona = useSessionStore((s) => s.persona);
  const sweeperOutcomes = useBrainStore((s) => s.sweeperOutcomes);
  const storeCustomers = useBrainStore((s) => s.customers);
  const storeBookings = useBrainStore((s) => s.bookings);
  const storeDeposits = useBrainStore((s) => s.deposits);
  const noRebookFlags = useBrainStore((s) => s.noRebookFlags);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const callbacks = useMemo(
    () => buildSweeperCallbacks({ outcomes: sweeperOutcomes, customers: storeCustomers, pid: currentActorId(), nowIso: clockIso }),
    // persona: a switch changes who the band is for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sweeperOutcomes, storeCustomers, clockIso, persona],
  );
  const unfinished = useMemo(
    () =>
      buildSweeperUnfinishedBusiness({
        noRebookFlags,
        pieces,
        bookings: storeBookings,
        deposits: storeDeposits,
        customers: storeCustomers,
        artists,
        nowIso: clockIso,
      }),
    [noRebookFlags, pieces, storeBookings, storeDeposits, storeCustomers, artists, clockIso],
  );

  return (
    <section className="px-10 py-8" data-testid="sweeper-screen">
      <header className="max-w-6xl">
        <p className="label-small-caps text-gold-bright">Sweeper</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Fill this week&rsquo;s gaps</h1>
        <p className="font-serif text-cream-soft mt-1">
          This week · every studio · one artist at a time.
        </p>
      </header>

      <SweeperCallbacks rows={callbacks} />

      <div className="mt-6 flex items-center gap-6 border-b border-gold-dim/25">
        <Stab label="Fill this week's gaps" active={tab === 'fill'} onClick={() => setTab('fill')} testid="sweeper-tab-fill" />
        <Stab
          label="Missed deposits"
          count={missed.length}
          active={tab === 'missed'}
          onClick={() => setTab('missed')}
          testid="sweeper-tab-missed"
        />
        <Stab
          label="Unfinished business"
          count={unfinished.length}
          active={tab === 'unfinished'}
          onClick={() => setTab('unfinished')}
          testid="sweeper-tab-unfinished"
        />
      </div>

      <div className="mt-6">
        {tab === 'fill' ? (
          <SweeperWorkspace />
        ) : tab === 'missed' ? (
          <MissedDepositsList rows={missed} />
        ) : (
          <UnfinishedBusinessList rows={unfinished} artists={artists} bookings={storeBookings} nowIso={clockIso} />
        )}
      </div>
    </section>
  );
}

function Stab({
  label,
  count,
  active,
  onClick,
  testid,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'flex items-center gap-2 pb-2.5 -mb-px border-b-2 text-[13px] font-semibold transition',
        active ? 'border-gold-bright text-cream' : 'border-transparent text-cream-faint hover:text-cream-soft',
      )}
    >
      {label}
      {count !== undefined && count > 0 ? (
        <span className="rounded-full bg-ink-soft px-1.5 py-px font-mono text-[10px] tabular-nums text-cream-soft">
          {count}
        </span>
      ) : null}
    </button>
  );
}
